import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthUser from '../services/useAuthUser';
import { useSubscription } from './useSubscription';
import { getPremiumProduct } from '../config/premiumProducts';

export function buildReturnToPath(location, overridePath) {
  if (overridePath) return overridePath;
  return `${location.pathname}${location.search}${location.hash}`;
}

export function resolvePremiumAccessState({
  authLoading,
  user,
  subscriptionLoading,
  entitlements,
  requiredEntitlement,
}) {
  if (authLoading) return 'auth_loading';
  if (!user) return 'signed_out';
  if (subscriptionLoading) return 'subscription_loading';
  if (entitlements.includes(requiredEntitlement)) return 'allowed';
  return 'signed_in_no_access';
}

export function usePremiumProductAccess(productKey) {
  const navigate = useNavigate();
  const location = useLocation();
  const product = useMemo(() => getPremiumProduct(productKey), [productKey]);
  const { user, loading: authLoading } = useAuthUser();
  const subscription = useSubscription();
  const [promptOptions, setPromptOptions] = useState(null);

  const accessState = resolvePremiumAccessState({
    authLoading,
    user,
    subscriptionLoading: subscription.loading,
    entitlements: subscription.entitlements,
    requiredEntitlement: product.entitlement,
  });

  const isLoading =
    accessState === 'auth_loading' || accessState === 'subscription_loading';

  const closePrompt = useCallback(() => {
    setPromptOptions(null);
  }, []);

  const attemptAccess = useCallback(
    (allowedAction, options = {}) => {
      if (
        accessState === 'allowed' ||
        (accessState === 'signed_in_no_access' && product.allowSignedInTrial)
      ) {
        allowedAction();
        return true;
      }
      setPromptOptions(options);
      return false;
    },
    [accessState, product.allowSignedInTrial]
  );

  const promptOpen = Boolean(promptOptions);
  const returnTo = buildReturnToPath(location, promptOptions?.returnTo);

  const handleSignIn = useCallback(() => {
    navigate(`/auth?redirect=${encodeURIComponent(returnTo)}`);
  }, [navigate, returnTo]);

  const handleUpgrade = useCallback(() => {
    navigate('/', {
      state: {
        scrollToPricing: true,
        highlight: product.pricingHighlight,
        track: product.track,
        product: product.key,
        returnTo,
      },
    });
  }, [navigate, product, returnTo]);

  const handleDemo = useCallback(() => {
    if (promptOptions?.onDemo) {
      promptOptions.onDemo();
      return;
    }

    if (product.demoState) {
      navigate('/', { state: product.demoState });
    }
  }, [navigate, product, promptOptions]);

  return {
    product,
    accessState,
    isAllowed: accessState === 'allowed',
    isLoading,
    attemptAccess,
    closePrompt,
    promptProps: {
      open: promptOpen,
      accessState,
      product,
      onClose: closePrompt,
      onSignIn: handleSignIn,
      onUpgrade: handleUpgrade,
      onDemo:
        promptOptions?.onDemo || product.demoState ? handleDemo : null,
      demoLabel: promptOptions?.demoLabel || product.demoLabel,
    },
    routePromptProps: {
      open: true,
      accessState,
      product,
      onClose: null,
      onSignIn: handleSignIn,
      onUpgrade: handleUpgrade,
      onDemo: product.demoState ? handleDemo : null,
      demoLabel: product.demoLabel,
    },
  };
}
